import React, { useState, FC } from 'react';
import { useStoreState } from 'easy-peasy';
import { Button, Input } from 'Components/nui';
import { useToggle } from 'react-use';
import { FormattedMessage, useIntl } from 'react-intl';
import MarketFilters from 'Components/filters/MarketFilters';
import { Icon, Popover, Menu, Dropdown } from 'antd';
import { useFilters } from 'Components/filters/context';
import {
  useSaveMarketplacePreset,
  useDeleteMarketplacePreset,
} from '~/hooks/useMarketplacePresets';
import { Dayjs } from 'dayjs';

interface FilterData {
  fDivision?: string[];
  fProducts?: string[];
  fTags?: string[];
  fType?: string[];
  fEtd?: Dayjs[];
  fEtdOption?: string;
  fUpdated?: Dayjs[];
  fUpdatedOption?: string;
}

interface NewFilterFormat {
  division?: { items: string[] };
  product?: { items: string[] };
  group?: { items: string[] };
  type?: { items: string[] };
  etd?: { items: Dayjs[] | undefined };
  etdOption?: { items: string | undefined };
  updated?: { items: Dayjs[] | undefined };
  updatedOption?: { items: string | undefined };
}

interface Solution {
  id: string;
}

interface MarketplacePreset {
  id: string;
  name: string;
  solution: string;
  data: FilterData;
}

interface UserPrefs {
  marketplace?: {
    presets?: MarketplacePreset[];
  };
}

interface StoreState {
  auth: {
    solution: Solution;
    userPrefs: UserPrefs;
  };
}

const legacyToNew = (data: FilterData): NewFilterFormat => {
  return {
    division: {
      items: data.fDivision ?? [],
    },
    product: {
      items: data.fProducts ?? [],
    },
    group: {
      items: data.fTags ?? [],
    },
    type: {
      items: data.fType ?? [],
    },
    etd: {
      items: data.fEtd,
    },
    etdOption: {
      items: data.fEtdOption,
    },
    updated: {
      items: data.fUpdated,
    },
    updatedOption: {
      items: data.fUpdatedOption,
    },
  };
};

const newToLegacy = (data: NewFilterFormat): FilterData => {
  return {
    fDivision: data.division?.items,
    fProducts: data.product?.items,
    fTags: data.group?.items,
    fType: data.type?.items,
    fEtd: data.etd?.items,
    fEtdOption: data.etdOption?.items,
    fUpdated: data.updated?.items,
    fUpdatedOption: data.updatedOption?.items,
  };
};

export const PresetsList: FC = () => {
  const { formatMessage } = useIntl();
  const solution = useStoreState((state: StoreState) => state.auth.solution);
  const prefs = useStoreState((state: StoreState) => state.auth.userPrefs);
  const presets = (prefs?.marketplace?.presets ?? [])
    .filter((item) => solution && item.solution === solution.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const deleteMarketplacePreset = useDeleteMarketplacePreset();

  const { setFilters } = useFilters();

  const applyPreset = (data: FilterData) => {
    setFilters(legacyToNew(data));
  };

  const handleDelete = (id: string) => {
    deleteMarketplacePreset.mutate(id);
  };

  return (
    <>
      {presets.length > 0 && (
        <Dropdown
          placement="bottomRight"
          overlay={
            <Menu className="filter-preset-dropdown">
              {presets.map((item) => (
                <Menu.Item key={item.id}>
                  <Button
                    type="buttonlink"
                    onClick={() => applyPreset(item.data)}
                  >
                    <span title={item.name}>{item.name}</span>
                  </Button>
                  <Button
                    type="buttonlink"
                    onClick={() => handleDelete(item.id)}
                    className="delete-button"
                  >
                    <span
                      className="icon-trash"
                      title={formatMessage({
                        id: 'delete-preset',
                        defaultMessage: 'Delete preset',
                      })}
                    ></span>
                  </Button>
                </Menu.Item>
              ))}
            </Menu>
          }
          trigger={['click']}
          className="tw:!ml-0 tw:mr-3"
        >
          <Button type="buttonlink">
            <FormattedMessage
              id="marketplace-filter-presets-heading"
              description="Heading for 'Filter presets' in Marketplace filter"
              defaultMessage="Filter presets"
            />{' '}
            <Icon type="caret-down" />
          </Button>
        </Dropdown>
      )}
    </>
  );
};

export const PresetForm: FC = () => {
  const { formatMessage } = useIntl();
  const saveMarketplacePreset = useSaveMarketplacePreset();
  const solution = useStoreState((state: StoreState) => state.auth.solution);
  const { filters } = useFilters();

  const [on, toggle] = useToggle(false);
  const [name, setName] = useState('');

  const save = async () => {
    if (!name) return;

    try {
      await saveMarketplacePreset.mutateAsync({
        name,
        solution: solution.id,
        data: newToLegacy(filters),
      });
      setName('');
      toggle(false);
    } catch {
      // Error already handled in the hook
    }
  };

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      visible={on}
      onVisibleChange={toggle}
      className="tw:mr-3"
      content={
        <div className="ant-input ant-input-wrap">
          <Input
            placeholder={formatMessage({
              id: 'name-for-preset',
              defaultMessage: 'Name for preset',
            })}
            className="nui-"
            value={name}
            onChange={(e) => setName(e.target.value)}
            ref={(input) => input && input.focus()}
            onEnter={() => name !== '' && save()}
          />
          <Button
            type="primary"
            onClick={save}
            disabled={name === '' || saveMarketplacePreset.isPending}
            size="small"
          >
            <FormattedMessage
              id="marketplace-filter-presets-field-save-button"
              description="Button for 'Save presets' in Marketplace filter"
              defaultMessage="Save"
            />
          </Button>
        </div>
      }
    >
      <Button type="buttonlink" onClick={() => toggle(true)}>
        <FormattedMessage
          id="marketplace-filter-presets-field-save-label"
          description="label for 'Save presets' in Marketplace filter"
          defaultMessage="Save as a preset"
        />{' '}
        <Icon type="caret-down" />
      </Button>
    </Popover>
  );
};

const Filter: FC = () => {
  return <MarketFilters />;
};

export default Filter;
